/**
 * DeleteAccountDialog — typed-phrase confirmation for self-service account
 * deletion. STORY-001-010 Implementation logic §4 + AC #8, #9.
 *
 * The user must type the localized confirm phrase (RU/KY/EN per ADR-028)
 * before "Delete permanently" enables. On confirm it calls POST /me/delete; on
 * 202 the caller clears in-memory auth and redirects.
 *
 * The confirm phrase shown is the one matching the user's current UI locale
 * (`account.delete.confirmPhrase`); the backend accepts ANY of the three
 * localized phrases, so a locale switch mid-flow still works.
 *
 * 2026-08 counter revamp — what changed and why:
 *   - The modal chrome (backdrop, focus trap, Escape, focus restore) moved to
 *     `DialogShell`/`useDialogA11y` so the log-out confirmation shares one
 *     implementation instead of a second near-copy. The a11y behaviour is
 *     unchanged; `axe-account-screens.spec.ts` gates it.
 *   - The consequences are now ENUMERATED as a ruled list before the input,
 *     not compressed into one grey sentence. This is the single most
 *     irreversible control in the product and the user is, by definition,
 *     someone who is not certain what it does.
 *   - The phrase to type is rendered in `.g-data` — it is a literal token to
 *     copy character-for-character, which is exactly what the instrument face
 *     is for.
 *
 * Every `data-testid` here is load-bearing (`account-delete.spec.ts`,
 * `PrivacyScreen.test.tsx`, `SettingsScreen.test.tsx`) and is preserved.
 */
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@garden/ui";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogShell } from "@/features/account/components/dialog";

export interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called when the user confirms; resolves false on failure (dialog stays open). */
  onConfirm: (confirmPhrase: string) => Promise<boolean>;
}

/** The three things deletion actually does, stated before the user can act. */
const CONSEQUENCE_KEYS = [
  "delete.consequences.profile",
  "delete.consequences.threads",
  "delete.consequences.sessions",
] as const;

export function DeleteAccountDialog({
  open,
  onClose,
  onConfirm,
}: DeleteAccountDialogProps): ReactElement | null {
  const { t } = useTranslation("account");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requiredPhrase = t("delete.confirmPhrase");

  useEffect(() => {
    if (open) {
      setTyped("");
      setBusy(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const phraseMatches = typed.trim() === requiredPhrase;

  const onSubmit = async (): Promise<void> => {
    if (!phraseMatches || busy) {
      if (!phraseMatches) setError("delete.phraseMismatch");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const ok = await onConfirm(typed.trim());
      if (!ok) {
        setError("delete.error");
        setBusy(false);
      }
      // On success the caller navigates away; leave busy=true so the button
      // doesn't re-enable during the redirect.
    } catch {
      setError("delete.error");
      setBusy(false);
    }
  };

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      title={t("delete.title")}
      tone="danger"
      closeLabel={t("delete.cancel")}
      testId="delete-account-dialog"
      backdropTestId="delete-account-backdrop"
      closeTestId="delete-account-close"
      titleId="delete-account-title"
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="delete-account-cancel"
          >
            {t("delete.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={busy}
            disabled={!phraseMatches}
            onClick={() => void onSubmit()}
            data-testid="delete-account-confirm"
          >
            {busy ? t("delete.deleting") : t("delete.confirm")}
          </Button>
        </>
      }
    >
      <p className="text-[14px] leading-[1.55] text-[var(--g-ink-2)]">
        {t("delete.warning")}
      </p>

      {/* The consequences, ruled — one line each, so none of them can be
          skimmed past inside a paragraph. */}
      <ul className="g-ruled mt-3.5 rounded-[var(--g-radius)] border border-[var(--g-rule)] bg-[var(--g-sunken)]">
        {CONSEQUENCE_KEYS.map((key) => (
          <li
            key={key}
            className="px-3.5 py-2.5 text-[13px] leading-snug text-[var(--g-ink-2)]"
          >
            {t(key)}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-col gap-1.5">
        <Label htmlFor="delete-confirm-input">{t("delete.confirmLabel")}</Label>
        <p className="text-[13px] leading-snug text-[var(--g-ink-2)]">
          {t("delete.confirmPrompt")}{" "}
          <span className="g-data rounded-[6px] border border-[var(--g-rule)] bg-[var(--g-surface)] px-1.5 py-0.5 text-[12.5px] font-semibold text-foreground">
            {requiredPhrase}
          </span>
        </p>
        <Input
          id="delete-confirm-input"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            if (error !== null) setError(null);
          }}
          placeholder={t("delete.confirmPlaceholder")}
          disabled={busy}
          aria-invalid={error !== null || undefined}
          data-testid="delete-confirm-input"
          autoComplete="off"
          className="min-h-11"
        />
        {error !== null ? (
          <p
            role="alert"
            className="text-[13px] font-semibold text-[var(--g-error)]"
            data-testid="delete-account-error"
          >
            {t(error)}
          </p>
        ) : null}
      </div>
    </DialogShell>
  );
}

export default DeleteAccountDialog;
